import * as Yup from 'yup';
import {
    validOfficeSysName,
    validUsPhoneNumber
} from './custom-validations.js';

const isFilled = (value) => value != null && String(value).trim() !== '';

const requiredWhenCopilot = Yup.string().when('isCopilotOffice', {
    is: true,
    then: (schema) => schema.trim().required('Required'),
    otherwise: (schema) => schema
});

/**
 * Yup validation schema for creating an office.
 *
 * @constant
 * @type {object}
 */
const createOfficeValidation = Yup.object({
    hubId: Yup.number().positive().integer().nullable(),
    officeName: Yup.string()
        .trim('Remove any leading or trailing spaces')
        .strict(),
    officeSysName: Yup.string()
        .required('Required')
        .matches(validOfficeSysName, {
            message:
                'Must match the following format: ut.shire.baggins, representing the state, city, and doctor last name. If multiple words, should be separated by underscore e.g. ut.the_shire.baggins. Should be all lowercase.'
        })
        .trim('Remove any leading or trailing spaces'),
    officeEmail: Yup.string().trim('Remove any leading or trailing spaces'),
    itName: Yup.string().trim('Remove any leading or trailing spaces'),
    itPhoneNumber: Yup.string()
        .trim('Remove any leading or trailing spaces')
        .matches(validUsPhoneNumber, {
            message:
                'Must be a valid US phone number in the following format: (801) 123-1234'
        }),
    itEmail: Yup.string().trim('Remove any leading or trailing spaces'),
    connectionSoftware: Yup.string().trim(
        'Remove any leading or trailing spaces'
    ),
    dentalSoftware: Yup.string().trim('Remove any leading or trailing spaces'),
    dsUsername: Yup.string(),
    dsPassword: Yup.string(),
    dsUsername2: Yup.string(),
    dsPassword2: Yup.string(),
    xrays: Yup.string().trim('Remove any leading or trailing spaces'),
    eClaims: Yup.string().trim('Remove any leading or trailing spaces'),
    eClaimsUsername: Yup.string(),
    eClaimsPassword: Yup.string(),
    eClaimsInstructions: Yup.string(),
    practiceBoosterUsername: Yup.string(),
    practiceBoosterPassword: Yup.string(),
    pdfPrinter: Yup.string().trim('Remove any leading or trailing spaces'),
    scannerInfo: Yup.string().trim('Remove any leading or trailing spaces'),
    attachments: Yup.string().trim('Remove any leading or trailing spaces'),
    phoneNumber: Yup.string()
        .trim('Remove any leading or trailing spaces')
        .matches(validUsPhoneNumber, {
            message:
                'Must be a valid US phone number in the following format: (801) 123-1234'
        }),
    officeHours: Yup.string().trim('Remove any leading or trailing spaces'),
    production: Yup.string().trim('Remove any leading or trailing spaces'),
    installTech: Yup.string().trim('Remove any leading or trailing spaces'),
    installDate: Yup.date().nullable(),
    numOfDBConnections: Yup.number().integer(),
    numOfIVConnections: Yup.number().integer(),
    numOfPPConnections: Yup.number().integer(),
    numOfFSConnections: Yup.number().integer(),
    eobPath: Yup.string(),
    notes: Yup.string(),
    uniqueVPN: Yup.object({
        hasUniqueVPN: Yup.boolean().required('Required'),
        addendumOnFile: Yup.boolean().required('Required')
    }),
    firewallInfo: Yup.string(),
    backupTimes: Yup.string(),
    poc: Yup.string().trim('Remove any leading or trailing spaces'),
    isKoc: Yup.boolean(),
    dsURL: Yup.string().trim('Remove any leading or trailing spaces'),
    isCopilotOffice: Yup.boolean(),
    isCompanionOffice: Yup.boolean(),
    copilotServerName: requiredWhenCopilot,
    superSyncComputer: Yup.string().when('isCompanionOffice', {
        is: true,
        then: (schema) => schema.trim().required('Required'),
        otherwise: (schema) => schema
    }),
    dxcAccountNumber: Yup.string(),
    edsPracticeId: Yup.string(),
    copilotNotes: Yup.string()
}).test(
    'dxc-or-eds-when-copilot',
    'Required: DXC Account # OR EDS Practice ID',
    function dxcOrEdsWhenCopilot(values) {
        if (!values?.isCopilotOffice) {
            return true;
        }

        const hasDxc = isFilled(values.dxcAccountNumber);
        const hasEds = isFilled(values.edsPracticeId);

        if (hasDxc || hasEds) {
            return true;
        }

        return this.createError({
            path: 'dxcAccountNumber',
            message: 'Required (or provide EDS Practice ID)'
        });
    }
);

export default createOfficeValidation;
